import { useMutation, UseMutationOptions } from '@tanstack/react-query';
import type {
  ConfigApiError,
  EnvironmentKey,
  DomainEntity,
  ConfigEntity,
  EntityRootData,
  ValidationEndpointResponse,
} from '@web-config-app/core';
import { isConfigEntity } from '@web-config-app/core';
import { API_JSON_CONTENT_TYPE } from '@web-config-app/api';
import { getEntityEndpoint } from '../../utilities/get-entity-endpoint/get-entity-endpoint.util';
import { useEnvironmentFetch } from '../use-environment-fetch/use-environment-fetch';
import { removeNullAndEmptyObjects } from '../../utilities/remove-null-and-empty-objects/remove-null-and-empty-objects';
import { OnMutate } from '../../types/operation.types';

export type UseEntityValidateOptions = Pick<
  UseMutationOptions<ValidationEndpointResponse, ConfigApiError, void>,
  'onError' | 'onSettled'
> & {
  onMutate?: OnMutate;
  onSuccess?: (data: ValidationEndpointResponse) => void;
  fetchFn?: (path: string, options: any) => Promise<Response>;
};

export interface UseEntityValidateProps {
  entity: ConfigEntity | DomainEntity;
  data?: EntityRootData;
  headers?: HeadersInit;
  environment: EnvironmentKey;
  options?: UseEntityValidateOptions;
}

/**
 * useEntityValidate
 * Lightweight mutation-style hook to invoke an entity's validate endpoint with an arbitrary payload.
 * Behavior:
 * - Lazy: nothing runs until you call mutate()/validate()/validateAsync.
 * - Sanitizes payload (removes null / empty objects) before sending as this is what is done before any operation.
 * - Automatically wires headers (defaults to JSON) and uses the entity's OPTIONAL configured validate endpoint.
 * - Optional fetch override via options.fetchFn.
 *
 * Expected server semantics (for consumer expectations):
 * - 200 OK    => { status: 'ok', data, requestId }
 * - 400 Error => { status: 'error', errorType: 'missing_required', errors: [...] }
 * - 422 Error => { status: 'error', errorType: 'invalid_value', errors: [...] }
 * - 500 Error => { status: 'error', errorType: 'internal', requestId }
 *
 *
 * Example:
 * const { mutate: validate, isPending, data: response, error } = useEntityValidate({ entity, environment, data: formData });
 * validate();
 *
 * Returned object spreads the underlying react-query mutation plus:
 * - enableAction: boolean flag indicating whether a validate endpoint exists on the entity definition.
 */

export const useEntityValidate = ({
  entity,
  environment,
  data,
  headers = { 'Content-Type': API_JSON_CONTENT_TYPE },
  options,
}: UseEntityValidateProps) => {
  // TODO(CACT-2876): the validate endpoint only exists on the wrapped ConfigEntity, and is not
  // wired up yet — until then this hook is a no-op.
  const validationOperation = isConfigEntity(entity)
    ? entity.endpoints.validate
    : undefined;

  const { apiUrl } = isConfigEntity(entity) ? entity.domainEntity : entity;

  const currentEnvironmentFetch = useEnvironmentFetch(environment, apiUrl);

  let mutationFn;

  if (validationOperation) {
    const { endpointFetch } = getEntityEndpoint<ValidationEndpointResponse>(
      validationOperation,
      options?.fetchFn ?? currentEnvironmentFetch,
    );

    mutationFn = () => {
      const sanitizedData = data ? removeNullAndEmptyObjects(data) : data;
      const body = JSON.stringify({
        data: sanitizedData,
      });
      return endpointFetch({
        body,
        headers,
      });
    };
  }

  const mutation = useMutation<
    ValidationEndpointResponse,
    ConfigApiError,
    void
  >({
    mutationFn,
    onSuccess: (validationResponse) => options?.onSuccess?.(validationResponse),
    onError: options?.onError,
    onMutate: () => options?.onMutate?.(),
    onSettled: options?.onSettled,
  });

  return {
    ...mutation,
    enableAction: Boolean(validationOperation),
  };
};
